import React, { useCallback, useEffect, useState } from 'react';
import { SettingsRow } from '@ofself/shell';
import Toggle from '../../components/nucleus/Switch';
import NucleusPage from '../../components/nucleus/NucleusPage';
import { Note } from '../../components/nucleus/Inspector';
import { useAuth } from '../../contexts/AuthContext';
import { Empty } from '../../components/nucleus/Empty';
import { useGroup } from '../../contexts/GroupContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { usePlace } from '../../nucleus/place';
import api from '../../services/api';
import { testPopUp } from '../../utils/browserNotify';
import { FormColumn, FormBand } from './selfForm';

// NOTIFICATIONS — how the platform reaches you, in the page grammar. Three lanes across the
// two channels that exist (the inbox, and pop-ups in this browser), this browser's permission,
// quiet hours, sound, a line per connected app and a mute per space. Every switch here is read
// by something: the server for the inbox, browserNotify for pop-ups, sound and quiet hours.
//
// THE INVARIANT: personalization moves a notification, it never deletes one. A
// queue row waits on an answer and a security row is a record; both always reach
// the inbox, so those two switches read "always". Every switch saves as it flips.

const LANES = [
    { id: 'queue', label: 'Needs you', hint: 'Invitations, questions, consent asks' },
    { id: 'feed', label: 'Happened', hint: 'Reads, writes, shares, finished work, from apps and groups' },
    { id: 'security', label: 'Security', hint: 'Sign-ins, credentials, keys, grants, membership' },
];
// Push is not offered: nothing delivers it. Stored push switches are left alone.
const CHANNELS = [
    { id: 'in_app', label: 'Inbox' },
    { id: 'browser', label: 'This browser' },
];
const ALWAYS_IN_APP = new Set(['queue', 'security']);
const APP_COLUMNS = [
    { id: 'reads', label: 'Reads and writes' },
    { id: 'activity', label: 'Finished work' },
    { id: 'mute', label: 'Mute' },
];

// What this browser lets pop-ups do, in words, with what can be done about it.
const PERMISSION = {
    granted: { word: 'allowed', says: 'Allowed in this browser. Each browser asks once.' },
    default: { word: null, says: 'Not asked yet. Each browser asks once.' },
    denied: { word: 'blocked', says: 'Blocked in this browser. Allow them in its site settings, then come back.' },
    unsupported: { word: 'unavailable', says: 'This browser cannot show pop-ups.' },
};

// A GRID OF SWITCHES: what is switched down the side, the channels named once
// across the top, one switch to a cell (SelfSection.css .self-grid).
function SwitchGrid({ columns, rows }) {
    return (
        <div className="self-grid" role="table" style={{ '--cols': columns.length }}>
            <div className="self-grid__row is-head" role="row">
                <span role="columnheader" aria-hidden="true" />
                {columns.map((c) => <span key={c.id} role="columnheader">{c.label}</span>)}
            </div>
            {rows.map((r) => (
                <div key={r.id} className="self-grid__row" role="row">
                    <span className="self-grid__label" role="rowheader"><b>{r.label}</b>{r.hint && <small>{r.hint}</small>}</span>
                    {columns.map((c) => <span key={c.id} className="self-grid__cell" role="cell">{r.cell(c)}</span>)}
                </div>
            ))}
        </div>
    );
}

export default function NotificationsPage() {
    const { section, page, to, navigate } = usePlace();
    const { groups } = useGroup();
    const { user } = useAuth();
    // The inbox's own provider: what the client announces by, and this browser's permission.
    const { applyPrefs, permission, askPermission } = useNotifications();
    const [settings, setSettings] = useState(null);
    const [apps, setApps] = useState([]);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const data = await api.getNotificationSettings();
            setSettings(data);
            // ONE TIMEZONE, the account's (the server reads quiet hours in it). Filled in
            // from this browser only when the account has none — never over a choice.
            const zone = data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (user && !user.timezone && zone) {
                try {
                    await api.updateUser(user.id, { timezone: zone });
                    setSettings((s) => ({ ...s, timezone: zone }));
                } catch { /* quiet hours fall back as before */ }
            }
        } catch {
            setError('Could not read your notification settings.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.request('/app-shell/apps')
            .then((r) => setApps((r?.apps || []).map((row) => row.app || row)))
            .catch(() => setApps([]));
    }, []);

    const save = async (patch) => {
        setError(null);
        const previous = settings;
        setSettings((s) => ({ ...s, ...patch }));
        try {
            const saved = await api.updateNotificationSettings(patch);
            setSettings(saved);
            applyPrefs?.(saved);
        } catch {
            setSettings(previous); // put it back rather than lie
            setError('That change did not save.');
        }
    };

    const body = () => {
        if (!settings) return error ? <Empty error>{error}</Empty> : null;
        const lanesFor = (lane) => ({ ...(settings.defaults?.lanes?.nucleus?.[lane] || {}), ...((settings.lanes?.nucleus || {})[lane] || {}) });
        const setChannel = (lane, channel, on) => save({
            lanes: {
                ...(settings.lanes || {}),
                nucleus: { ...(settings.lanes?.nucleus || {}), [lane]: { ...((settings.lanes?.nucleus || {})[lane] || {}), [channel]: on } },
            },
        });
        const appRules = (id) => ({ ...(settings.defaults?.app || {}), ...((settings.apps || {})[id] || {}) });
        const setApp = (id, key, on) => save({ apps: { ...(settings.apps || {}), [id]: { ...appRules(id), [key]: on } } });
        // Groups and apps share one muted list: each id is only matched against its own kind.
        const muted = settings.muted_spaces || [];
        const toggleMuted = (id) => save({ muted_spaces: muted.includes(id) ? muted.filter((g) => g !== id) : [...muted, id] });
        const allowed = PERMISSION[permission] || PERMISSION.unsupported;

        return (
            <FormColumn>
                {error && <Note tone="live">{error}</Note>}
                <FormBand title="Lanes" lead="Needs you and Security always reach your inbox. You choose what also pops up in this browser. Announcements from ofSelf always arrive.">
                    <SwitchGrid
                        columns={CHANNELS}
                        rows={LANES.map((lane) => {
                            const on = lanesFor(lane.id);
                            return {
                                id: lane.id, label: lane.label, hint: lane.hint,
                                cell: (c) => (ALWAYS_IN_APP.has(lane.id) && c.id === 'in_app'
                                    ? <span className="self-always">always</span>
                                    : <Toggle checked={!!on[c.id]} aria-label={`${lane.label} ${c.label}`} onChange={(e) => setChannel(lane.id, c.id, e.target.checked)} />),
                            };
                        })}
                    />
                </FormBand>

                <FormBand
                    title="This browser"
                    lead="Pop-ups are per browser: turning them on here does not turn them on in the Mac app."
                    acts={[{
                        id: 'test', label: 'Test a pop-up', disabled: permission !== 'granted',
                        run: () => { if (!testPopUp()) throw new Error('This browser did not show it.'); },
                    }]}
                >
                    <SettingsRow className="self-setting" label="Pop-ups" description={allowed.says}
                        control={allowed.word
                            ? <span className="self-always">{allowed.word}</span>
                            : <button type="button" className="nx-link" onClick={askPermission}>Allow</button>} />
                </FormBand>

                <FormBand title="Quiet">
                    <SettingsRow className="self-setting" label="Quiet hours" description="No sound and no pop-ups between these. Inbox still fills. Security ignores them."
                        control={(
                            <span className="self-switches">
                                <input type="time" className="self-input" aria-label="Quiet hours start" value={settings.quiet_hours_start || ''}
                                    onChange={(e) => save({ quiet_hours_start: e.target.value })} />
                                <span className="self-always">to</span>
                                <input type="time" className="self-input" aria-label="Quiet hours end" value={settings.quiet_hours_end || ''}
                                    onChange={(e) => save({ quiet_hours_end: e.target.value })} />
                            </span>
                        )} />
                    {/* The timezone is the account's: set on Profile, read here. */}
                    <SettingsRow className="self-setting" label="Read in" description="Quiet hours follow your account’s timezone."
                        control={(
                            <span className="self-switches">
                                <span className="self-always">{settings.timezone || 'UTC'}</span>
                                <button type="button" className="nx-link" onClick={() => navigate(to('self', 'profile'))}>Change on Profile</button>
                            </span>
                        )} />
                    <SettingsRow className="self-setting" label="Sound" description="A chime for Needs you and Security, once per event."
                        control={<Toggle checked={!!settings.sound_enabled} aria-label="Sound" onChange={(e) => save({ sound_enabled: e.target.checked })} />} />
                </FormBand>

                <FormBand title="Apps" aside={apps.length || undefined} lead="Each app follows the lanes until you change it here. Mute hides the messages it sends you; its asks and security events still arrive.">
                    {apps.length ? (
                        <SwitchGrid
                            columns={APP_COLUMNS}
                            rows={apps.map((a) => {
                                const id = String(a.id);
                                const rules = appRules(id);
                                const name = a.name || 'An app';
                                const off = muted.includes(id);
                                return {
                                    id, label: name, hint: off ? 'Muted' : ((settings.apps || {})[id] ? 'Its own settings' : 'Following the lanes'),
                                    cell: (c) => (c.id === 'mute'
                                        ? <Toggle checked={off} aria-label={`${name} ${c.label}`} onChange={() => toggleMuted(id)} />
                                        : <Toggle checked={!!rules[c.id]} aria-label={`${name} ${c.label}`} onChange={(e) => setApp(id, c.id, e.target.checked)} />),
                                };
                            })}
                        />
                    ) : <p className="nx-note">No apps are connected. Each one you connect gets a line here.</p>}
                </FormBand>

                <FormBand title="Spaces" aside={(groups || []).length || undefined} lead="A muted group still asks you things; its activity stays out of your inbox.">
                    {(groups || []).length ? (
                        <SwitchGrid
                            columns={[{ id: 'show', label: 'Show' }]}
                            rows={groups.map((g) => {
                                const off = muted.includes(String(g.id));
                                return {
                                    id: String(g.id), label: g.name, hint: off ? 'Muted' : 'All lanes',
                                    cell: () => <Toggle checked={!off} aria-label={`${g.name} notifications`} onChange={() => toggleMuted(String(g.id))} />,
                                };
                            })}
                        />
                    ) : <p className="nx-note">You are not in any groups. Each one you join gets a line here.</p>}
                </FormBand>
            </FormColumn>
        );
    };

    return <NucleusPage section={section.label} title={page.label}>{body()}</NucleusPage>;
}
